import React, { useEffect, useMemo, useRef, useState } from "react";
import { previousMonthValue } from "../reportPeriod";
import {
    createSupplier,
    downloadSupplierOrdersExport,
    downloadSupplierReconciliationReport,
    getBusinessStores,
    getBusinessSupplierMarkupReport,
    getBusinessStoreSupplierSettings,
    getSupplierReconciliationOptions,
    getBusinessSupplierStoreSettingsOverview,
    getSupplierViewDetail,
    getSuppliersViewList,
    runSupplierReconciliation,
    upsertBusinessStoreSupplierSettings,
    updateSupplier,
} from "../api/suppliersApi";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import {
    createSupplierDepositGroup,
    createSupplierDepositLink,
    deleteSupplierDepositCheckpoint,
    deleteSupplierDepositGroup,
    deleteSupplierDepositLink,
    getSupplierDepositGroups,
    getSupplierDepositReport,
    updateSupplierDepositGroup,
    upsertSupplierDepositCheckpoint,
} from "../api/supplierDepositsApi";
import { SpCheck, SpInput, SpReadField, SpSelect } from "../components/SpPrimitives";
import {
    deleteCounterpartyDropshipLink,
    listCounterparties,
    upsertCounterpartyDropshipLink,
} from "../api/counterpartiesApi";
import CounterpartiesPanel from "./CounterpartiesPanel";
import FulfillmentPage from "./FulfillmentPage";
import "./SuppliersPage.css";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "20px",
    maxWidth: "1500px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const SUPPLIER_CITY_OPTIONS = [
    "Вінниця", "Дніпро", "Донецьк", "Житомир", "Запоріжжя", "Івано-Франківськ",
    "Київ", "Кропивницький", "Луганськ", "Луцьк", "Львів", "Миколаїв", "Одеса",
    "Полтава", "Рівне", "Суми", "Тернопіль", "Ужгород", "Харків", "Херсон",
    "Хмельницький", "Черкаси", "Чернівці", "Чернігів",
].map((city) => ({ value: city, label: city }));

const cardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #d9dee8",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};

const truncateText = (value, maxLength = 56) => {
    const text = String(value || "").trim();
    if (!text) {
        return "—";
    }
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.slice(0, maxLength - 1)}…`;
};

const createDefaultShippingSchedule = () => Array.from(
    { length: 7 },
    (_, index) => ({
        weekday: index + 1,
        mode: "not_configured",
        order_cutoff_time: "",
        shipping_time: "",
    }),
);

const normalizeShippingSchedule = (value) => {
    const byWeekday = new Map(
        (Array.isArray(value) ? value : []).map((item) => [Number(item?.weekday), item]),
    );
    return createDefaultShippingSchedule().map((fallback) => {
        const item = byWeekday.get(fallback.weekday);
        return item ? {
            weekday: fallback.weekday,
            mode: item.mode || "not_configured",
            order_cutoff_time: item.order_cutoff_time || "",
            shipping_time: item.shipping_time || "",
        } : fallback;
    });
};

const defaultDraft = {
    is_active: true,
    code: "",
    name: "",
    city: "",
    salesdrive_supplier_id: "",
    biotus_orders_enabled: false,
    np_fulfillment_enabled: false,
    schedule_enabled: false,
    block_start_day: "",
    block_start_time: "",
    block_end_day: "",
    block_end_time: "",
    shipping_schedule: createDefaultShippingSchedule(),
    feed_url: "",
    gdrive_folder: "",
    is_rrp: false,
    profit_percent: "",
    retail_markup: "",
    min_markup_threshold: "",
    priority: 5,
    publish_priority_tier: 0,
    use_feed_instead_of_gdrive: false,
    order_ttn_mode: "auto",
};

const defaultStoreSettingsDraft = {
    supplier_code: "",
    is_active: true,
    is_rrp: false,
    priority_override: "",
    min_markup_threshold: "0",
    price_opt_calc_percent: "",
    extra_markup_enabled: false,
    extra_markup_mode: "percent",
    extra_markup_value: "",
    extra_markup_min: "",
    extra_markup_max: "",
    dumping_mode: false,
};

const boolPill = (value) => (
    <span className={value ? "sp-pill g" : "sp-pill r"}>{value ? "Да" : "Нет"}</span>
);

const today = new Date();
const defaultPeriodFrom = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
const defaultPeriodTo = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
const defaultMonthPeriod = previousMonthValue(today);
const previousDayValue = (now = new Date()) => {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
};
const MONTH_NAMES_RU = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
];

const buildMonthPeriodOptions = (count = 36) => {
    const base = new Date(today.getFullYear(), today.getMonth(), 1);
    return Array.from({ length: count }, (_, index) => {
        const monthDate = new Date(base.getFullYear(), base.getMonth() - index, 1);
        const value = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
        return {
            value,
            label: `${MONTH_NAMES_RU[monthDate.getMonth()]} ${monthDate.getFullYear()}`,
        };
    });
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return String(value);
    }
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatStoreLabel = (store) => {
    if (!store) {
        return "";
    }
    const parts = [store.store_name || store.store_code || "Магазин"];
    if (store.store_code) {
        parts.push(store.store_code);
    }
    if (store.tabletki_branch) {
        parts.push(`Branch ${store.tabletki_branch}`);
    }
    return parts.join(" · ");
};

const DAY_OPTIONS = [
    { value: "1", label: "Понедельник (Mon)" },
    { value: "2", label: "Вторник (Tue)" },
    { value: "3", label: "Среда (Wed)" },
    { value: "4", label: "Четверг (Thu)" },
    { value: "5", label: "Пятница (Fri)" },
    { value: "6", label: "Суббота (Sat)" },
    { value: "7", label: "Воскресенье (Sun)" },
];

const SHIPPING_SCHEDULE_DAY_LABELS = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
const SHIPPING_SCHEDULE_MODE_OPTIONS = [
    { value: "not_configured", label: "Не задано" },
    { value: "not_shipping", label: "Не отправляет" },
    { value: "around_the_clock", label: "Круглосуточно" },
    { value: "scheduled", label: "По расписанию" },
];

/* Поля-примитивы sp-* вынесены в ../components/SpPrimitives.jsx */

const SupplierDepositsPanel = ({ suppliers }) => {
    const [periodFrom, setPeriodFrom] = useState(defaultPeriodFrom);
    const [periodTo, setPeriodTo] = useState(defaultPeriodTo);
    const [report, setReport] = useState(null);
    const [groups, setGroups] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [groupDraft, setGroupDraft] = useState({
        id: null,
        name: "",
        description: "",
        is_active: true,
        display_order: 100,
        settlement_mode: "prepaid",
    });
    const [linkDraft, setLinkDraft] = useState({
        group_id: "",
        business_entity_id: "",
        supplier_code: "",
        valid_from: "",
        valid_to: "",
        notes: "",
    });
    const [checkpointDrafts, setCheckpointDrafts] = useState({});
    const [commentEditors, setCommentEditors] = useState({});
    const [showMovements, setShowMovements] = useState(true);
    const [depositTab, setDepositTab] = useState("summary");
    const supplierOptions = useMemo(
        () => [...(suppliers || [])].sort((left, right) => String(left.code).localeCompare(String(right.code))),
        [suppliers],
    );

    const loadDeposits = async () => {
        setLoading(true);
        setError("");
        try {
            const [reportData, groupsData, organizationsData] = await Promise.all([
                getSupplierDepositReport({ periodFrom, periodTo }),
                getSupplierDepositGroups(),
                getBusinessOrganizations({ is_active: true }),
            ]);
            setReport(reportData);
            setGroups(Array.isArray(groupsData) ? groupsData : []);
            setOrganizations(Array.isArray(organizationsData) ? organizationsData : []);
        } catch (loadError) {
            console.error("Ошибка загрузки депозитов:", loadError);
            setError(loadError?.response?.data?.detail || "Не удалось загрузить депозиты.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDeposits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetGroupDraft = () => {
        setGroupDraft({
            id: null,
            name: "",
            description: "",
            is_active: true,
            display_order: 100,
            settlement_mode: "prepaid",
        });
    };

    const editGroup = (group) => {
        setGroupDraft({
            id: group.id,
            name: group.name || "",
            description: group.description || "",
            is_active: Boolean(group.is_active),
            display_order: group.display_order ?? 100,
            settlement_mode: group.settlement_mode || "prepaid",
        });
    };

    const saveGroup = async () => {
        setError("");
        setSuccess("");
        const payload = {
            name: String(groupDraft.name || "").trim(),
            description: String(groupDraft.description || "").trim() || null,
            is_active: Boolean(groupDraft.is_active),
            display_order: Number(groupDraft.display_order || 100),
            settlement_mode: groupDraft.settlement_mode === "agency" ? "agency" : "prepaid",
        };
        if (!payload.name) {
            setError("Название депозитной группы обязательно.");
            return;
        }
        try {
            if (groupDraft.id) {
                await updateSupplierDepositGroup(groupDraft.id, payload);
                setSuccess("Депозитная группа обновлена.");
            } else {
                await createSupplierDepositGroup(payload);
                setSuccess("Депозитная группа создана.");
            }
            resetGroupDraft();
            await loadDeposits();
        } catch (saveError) {
            console.error("Ошибка сохранения депозитной группы:", saveError);
            setError(saveError?.response?.data?.detail || "Не удалось сохранить депозитную группу.");
        }
    };

    const removeGroup = async (groupId) => {
        if (!window.confirm("Удалить депозитную группу и все ее связи?")) {
            return;
        }
        setError("");
        setSuccess("");
        try {
            await deleteSupplierDepositGroup(groupId);
            setSuccess("Депозитная группа удалена.");
            await loadDeposits();
        } catch (deleteError) {
            console.error("Ошибка удаления депозитной группы:", deleteError);
            setError(deleteError?.response?.data?.detail || "Не удалось удалить депозитную группу.");
        }
    };

    const saveLink = async () => {
        setError("");
        setSuccess("");
        const payload = {
            group_id: Number(linkDraft.group_id),
            business_entity_id: Number(linkDraft.business_entity_id),
            supplier_code: String(linkDraft.supplier_code || "").trim(),
            is_active: true,
            valid_from: String(linkDraft.valid_from || "").trim() || null,
            valid_to: String(linkDraft.valid_to || "").trim() || null,
            notes: String(linkDraft.notes || "").trim() || null,
        };
        if (!payload.group_id || !payload.business_entity_id || !payload.supplier_code) {
            setError("Для связи выберите группу, юрлицо и отправителя.");
            return;
        }
        try {
            await createSupplierDepositLink(payload);
            setSuccess("Связь депозита добавлена.");
            setLinkDraft({
                group_id: "",
                business_entity_id: "",
                supplier_code: "",
                valid_from: "",
                valid_to: "",
                notes: "",
            });
            await loadDeposits();
        } catch (saveError) {
            console.error("Ошибка сохранения связи депозита:", saveError);
            setError(saveError?.response?.data?.detail || "Не удалось сохранить связь депозита.");
        }
    };

    const removeLink = async (linkId) => {
        setError("");
        setSuccess("");
        try {
            await deleteSupplierDepositLink(linkId);
            setSuccess("Связь депозита удалена.");
            await loadDeposits();
        } catch (deleteError) {
            console.error("Ошибка удаления связи депозита:", deleteError);
            setError(deleteError?.response?.data?.detail || "Не удалось удалить связь депозита.");
        }
    };

    const updateCheckpointDraft = (group, field, value) => {
        setCheckpointDrafts((prev) => ({
            ...prev,
            [group.id]: {
                balance_date: group.balance_checkpoint_date || periodTo,
                actual_balance: group.balance_checkpoint_amount || "",
                comment: "",
                ...(prev[group.id] || {}),
                [field]: value,
            },
        }));
    };

    const checkpointValue = (group, field, fallback = "") =>
        checkpointDrafts[group.id]?.[field] ?? fallback;

    const saveCheckpoint = async (group) => {
        const draft = checkpointDrafts[group.id] || {};
        setError("");
        setSuccess("");
        try {
            await upsertSupplierDepositCheckpoint({
                group_id: group.id,
                balance_date: draft.balance_date || group.balance_checkpoint_date || periodTo,
                actual_balance: draft.actual_balance || group.balance_checkpoint_amount || "0",
                comment: draft.comment || null,
                created_by: localStorage.getItem("user_login") || "admin-panel",
            });
            setSuccess("Фактический остаток депозита сохранен.");
            setCommentEditors((prev) => ({ ...prev, [group.id]: false }));
            await loadDeposits();
        } catch (saveError) {
            console.error("Ошибка сохранения факта депозита:", saveError);
            setError(saveError?.response?.data?.detail || "Не удалось сохранить фактический остаток.");
        }
    };

    const removeCheckpoint = async (group) => {
        if (!group.balance_checkpoint_id) {
            return;
        }
        if (!window.confirm(`Удалить факт депозита ${group.name} за ${group.balance_checkpoint_date}?`)) {
            return;
        }
        setError("");
        setSuccess("");
        try {
            await deleteSupplierDepositCheckpoint(group.balance_checkpoint_id);
            setSuccess("Фактический остаток депозита удален.");
            setCheckpointDrafts((prev) => {
                const next = { ...prev };
                delete next[group.id];
                return next;
            });
            await loadDeposits();
        } catch (deleteError) {
            console.error("Ошибка удаления факта депозита:", deleteError);
            setError(deleteError?.response?.data?.detail || "Не удалось удалить фактический остаток.");
        }
    };

    const reportGroups = report?.groups || [];
    const movementCount = reportGroups.reduce((total, group) => total + (group.movements || []).length, 0);

    return (
        <div className="sp-section">
            <div className="sp-card" style={{ padding: "14px" }}>
                <div className="sp-tabbar" role="tablist">
                    <button
                        type="button"
                        className={depositTab === "summary" ? "sp-tab active" : "sp-tab"}
                        onClick={() => setDepositTab("summary")}
                    >
                        Сводка
                    </button>
                    <button
                        type="button"
                        className={depositTab === "movements" ? "sp-tab active" : "sp-tab"}
                        onClick={() => setDepositTab("movements")}
                    >
                        Движения
                        {movementCount > 0 ? <span className="count">{movementCount}</span> : null}
                    </button>
                    <button
                        type="button"
                        className={depositTab === "settings" ? "sp-tab active" : "sp-tab"}
                        onClick={() => setDepositTab("settings")}
                    >
                        Настройки
                    </button>
                </div>
            </div>

            {error ? <div className="sp-card" style={{ padding: "12px 16px" }}><span className="sp-msg err">{error}</span></div> : null}
            {success ? <div className="sp-card" style={{ padding: "12px 16px" }}><span className="sp-msg ok">{success}</span></div> : null}

            {depositTab === "summary" ? (
            <>
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Депозиты поставщиков</h2>
                        <p>
                            Расчёт: фактический остаток на конец даты + исходящие оплаты поставщику − закупочная сумма активных заказов и продаж после этой даты.
                        </p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <SpInput label="С" type="date" value={periodFrom} onChange={setPeriodFrom} />
                        <SpInput label="По" type="date" value={periodTo} onChange={setPeriodTo} />
                        <button type="button" className="sp-btn sp-btn-ghost" disabled={loading} onClick={loadDeposits}>
                            Загрузить
                        </button>
                    </div>
                </div>
                {loading ? <div className="sp-mini">Загрузка депозитов…</div> : null}
            </div>

            <div className="sp-card" style={{ padding: "16px 18px", display: "grid", gap: "14px" }}>
                <h2 className="sp-h2">Сводка депозитов</h2>
                <div className="sp-tbl-wrap">
                    <table className="sp-tbl">
                        <thead>
                            <tr>
                                <th>Депозит</th>
                                <th>Связи</th>
                                <th>Факт на конец даты</th>
                                <th>Начало</th>
                                <th>Пополнения</th>
                                <th>Списания</th>
                                <th>Расчётный остаток</th>
                                <th>Зафиксировать факт на конец даты</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportGroups.map((group) => (
                                <tr key={group.id}>
                                    <td>
                                        <strong>{group.name}</strong>
                                        <div className="sp-mini">{group.description || "—"}</div>
                                    </td>
                                    <td style={{ minWidth: "240px" }}>
                                        <div className="sp-cell-list">
                                            {(group.active_links || []).map((link) => (
                                                <div key={link.id}>
                                                    {link.business_entity_name} · {link.supplier_code} {link.supplier_name || ""}
                                                </div>
                                            ))}
                                            {(group.active_links || []).length === 0 ? <span className="sp-mini">Нет активных связей</span> : null}
                                        </div>
                                    </td>
                                    <td>
                                        {formatAmount(group.balance_checkpoint_amount)}
                                        <div className="sp-mini">{group.balance_checkpoint_date || "нет факта"}</div>
                                        {group.balance_checkpoint_comment ? (
                                            <div className="sp-mini" style={{ marginTop: "4px" }}>{group.balance_checkpoint_comment}</div>
                                        ) : null}
                                    </td>
                                    <td>{formatAmount(group.opening_balance)}</td>
                                    <td>
                                        {formatAmount(group.topups?.amount)}
                                        <div className="sp-mini">{group.topups?.count || 0} платежей</div>
                                    </td>
                                    <td>
                                        {formatAmount(group.writeoffs?.amount)}
                                        <div className="sp-mini">{group.writeoffs?.count || 0} заказов</div>
                                    </td>
                                    <td className="sp-strong">{formatAmount(group.calculated_balance)}</td>
                                    <td style={{ minWidth: "650px" }}>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "140px minmax(150px, 1fr) auto auto auto",
                                                gap: "8px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <input
                                                className="sp-inp"
                                                type="date"
                                                value={checkpointValue(group, "balance_date", group.balance_checkpoint_date || periodTo)}
                                                onChange={(event) => updateCheckpointDraft(group, "balance_date", event.target.value)}
                                            />
                                            <input
                                                className="sp-inp"
                                                type="number"
                                                step="0.01"
                                                placeholder="Факт на конец даты"
                                                value={checkpointValue(group, "actual_balance", group.balance_checkpoint_amount || "")}
                                                onChange={(event) => updateCheckpointDraft(group, "actual_balance", event.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="sp-btn sp-btn-ghost sp-btn-sm"
                                                onClick={() => setCommentEditors((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                                            >
                                                Комментарий
                                            </button>
                                            <button type="button" className="sp-btn sp-btn-primary sp-btn-sm" onClick={() => saveCheckpoint(group)}>
                                                Сохранить
                                            </button>
                                            <button
                                                type="button"
                                                className="sp-btn sp-btn-ghost sp-btn-sm"
                                                disabled={!group.balance_checkpoint_id}
                                                onClick={() => removeCheckpoint(group)}
                                            >
                                                Удалить факт
                                            </button>
                                        </div>
                                        {commentEditors[group.id] ? (
                                            <input
                                                className="sp-inp"
                                                style={{ marginTop: "8px" }}
                                                placeholder="Комментарий к фиксации факта"
                                                value={checkpointValue(group, "comment", group.balance_checkpoint_comment || "")}
                                                onChange={(event) => updateCheckpointDraft(group, "comment", event.target.value)}
                                            />
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                            {reportGroups.length === 0 ? (
                                <tr><td colSpan={8} className="sp-mini">Депозитные группы пока не настроены.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
            </>
            ) : null}

            {depositTab === "movements" ? (
            <div className="sp-card" style={{ padding: "16px 18px", display: "grid", gap: "14px" }}>
                <div className="sp-toolbar-head">
                    <div>
                        <h2 className="sp-h2">Движения за период</h2>
                        <p className="sp-hint" style={{ marginTop: "4px" }}>Всего движений: {movementCount}</p>
                    </div>
                    <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setShowMovements((value) => !value)}>
                        {showMovements ? "Скрыть движения" : "Показать движения"}
                    </button>
                </div>
                {showMovements ? (
                    <div className="sp-tbl-wrap">
                        <table className="sp-tbl">
                            <thead>
                                <tr>
                                    <th>Дата</th>
                                    <th>Депозит</th>
                                    <th>Тип</th>
                                    <th>Юрлицо</th>
                                    <th>Поставщик</th>
                                    <th>Описание</th>
                                    <th>Сумма</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportGroups.flatMap((group) =>
                                    (group.movements || []).map((movement, index) => (
                                        <tr key={`${group.id}-${movement.type}-${movement.source_id}-${index}`}>
                                            <td>{movement.date?.slice(0, 10) || "—"}</td>
                                            <td>{group.name}</td>
                                            <td>{movement.type === "topup" ? "Пополнение" : "Списание"}</td>
                                            <td>{movement.business_entity_name || "—"}</td>
                                            <td>{movement.supplier_code} · {movement.supplier_name || "—"}</td>
                                            <td style={{ maxWidth: "420px" }}>{movement.description || movement.counterparty_name || "—"}</td>
                                            <td>{formatAmount(movement.amount)}</td>
                                        </tr>
                                    ))
                                )}
                                {movementCount === 0 ? (
                                    <tr><td colSpan={7} className="sp-mini">За выбранный период движений нет.</td></tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </div>
            ) : null}

            {depositTab === "settings" ? (
            <div className="sp-card" style={{ padding: "16px 18px", display: "grid", gap: "16px" }}>
                <h2 className="sp-h2">Настройки депозитов</h2>

                <div className="sp-block">
                    <div className="sp-bh"><span className="ic">＋</span><h4>Депозитная группа</h4></div>
                    <div
                        className="sp-form-grid"
                        style={{ gridTemplateColumns: "minmax(200px, 1fr) minmax(200px, 1fr) 100px minmax(180px, 0.9fr) auto auto" }}
                    >
                        <SpInput
                            label="Название группы"
                            value={groupDraft.name}
                            onChange={(value) => setGroupDraft((prev) => ({ ...prev, name: value }))}
                            placeholder="Например: Биотус общий"
                        />
                        <SpInput
                            label="Описание"
                            value={groupDraft.description}
                            onChange={(value) => setGroupDraft((prev) => ({ ...prev, description: value }))}
                        />
                        <SpInput
                            label="Порядок"
                            type="number"
                            value={groupDraft.display_order}
                            onChange={(value) => setGroupDraft((prev) => ({ ...prev, display_order: value }))}
                        />
                        <SpSelect
                            label="Тип расчётов"
                            value={groupDraft.settlement_mode}
                            onChange={(value) => setGroupDraft((prev) => ({ ...prev, settlement_mode: value }))}
                            placeholder={null}
                            options={[
                                { value: "prepaid", label: "Предоплатный (депозит)" },
                                { value: "agency", label: "Агентский (комиссия)" },
                            ]}
                        />
                        <SpCheck
                            label="Активна"
                            checked={Boolean(groupDraft.is_active)}
                            onChange={(value) => setGroupDraft((prev) => ({ ...prev, is_active: value }))}
                        />
                        <div style={{ display: "flex", gap: "8px", alignSelf: "end" }}>
                            <button type="button" className="sp-btn sp-btn-primary" onClick={saveGroup}>
                                {groupDraft.id ? "Обновить" : "Создать"}
                            </button>
                            {groupDraft.id ? (
                                <button type="button" className="sp-btn sp-btn-ghost" onClick={resetGroupDraft}>
                                    Отмена
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="sp-block">
                    <div className="sp-bh"><span className="ic">⇄</span><h4>Связать депозит</h4></div>
                    <div
                        className="sp-form-grid"
                        style={{ gridTemplateColumns: "repeat(3, minmax(180px, 1fr)) repeat(2, minmax(140px, 0.7fr)) auto" }}
                    >
                        <SpSelect
                            label="Группа"
                            value={linkDraft.group_id}
                            onChange={(value) => setLinkDraft((prev) => ({ ...prev, group_id: value }))}
                            options={groups.map((group) => ({ value: String(group.id), label: group.name }))}
                        />
                        <SpSelect
                            label="Юрлицо"
                            value={linkDraft.business_entity_id}
                            onChange={(value) => setLinkDraft((prev) => ({ ...prev, business_entity_id: value }))}
                            options={organizations.map((organization) => ({
                                value: String(organization.id),
                                label: `${organization.short_name}${organization.tax_id ? ` · ${organization.tax_id}` : ""}`,
                            }))}
                        />
                        <SpSelect
                            label="Отправитель"
                            value={linkDraft.supplier_code}
                            onChange={(value) => setLinkDraft((prev) => ({ ...prev, supplier_code: value }))}
                            options={supplierOptions.map((supplier) => ({
                                value: supplier.code,
                                label: `${supplier.code} · ${supplier.name || supplier.display_name}`,
                            }))}
                        />
                        <SpInput
                            label="Действует с"
                            type="date"
                            value={linkDraft.valid_from}
                            onChange={(value) => setLinkDraft((prev) => ({ ...prev, valid_from: value }))}
                        />
                        <SpInput
                            label="Действует по"
                            type="date"
                            value={linkDraft.valid_to}
                            onChange={(value) => setLinkDraft((prev) => ({ ...prev, valid_to: value }))}
                        />
                        <button type="button" className="sp-btn sp-btn-primary" style={{ alignSelf: "end" }} onClick={saveLink}>
                            Добавить связь
                        </button>
                    </div>
                </div>

                <div className="sp-tbl-wrap">
                    <table className="sp-tbl">
                        <thead>
                            <tr>
                                <th>Группа</th>
                                <th>Статус</th>
                                <th>Связи</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((group) => (
                                <tr key={group.id}>
                                    <td>
                                        <strong>{group.name}</strong>
                                        {group.settlement_mode === "agency" ? (
                                            <span className="sp-pill warn" style={{ marginLeft: "8px" }}>Агентский</span>
                                        ) : null}
                                        <div className="sp-mini">{group.description || "—"}</div>
                                    </td>
                                    <td>{group.is_active ? boolPill(true) : <span className="sp-pill off">Выключена</span>}</td>
                                    <td style={{ minWidth: "360px" }}>
                                        <div className="sp-cell-list">
                                            {(group.links || []).map((link) => (
                                                <div key={link.id} className="row">
                                                    <span>{link.business_entity_name} · {link.supplier_code} {link.supplier_name || ""}</span>
                                                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => removeLink(link.id)}>
                                                        Убрать
                                                    </button>
                                                </div>
                                            ))}
                                            {(group.links || []).length === 0 ? <span className="sp-mini">Связей нет</span> : null}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                            <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => editGroup(group)}>
                                                Редактировать
                                            </button>
                                            <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => removeGroup(group.id)}>
                                                Удалить
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {groups.length === 0 ? (
                                <tr><td colSpan={4} className="sp-mini">Создайте первую депозитную группу.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
            ) : null}
        </div>
    );
};

const SupplierMarkupsPanel = ({ onOpenStoreSettings }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [supplierFilter, setSupplierFilter] = useState("");
    const [storeFilter, setStoreFilter] = useState("");
    const [showOnlyMissing, setShowOnlyMissing] = useState(false);

    const loadReport = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getBusinessSupplierMarkupReport();
            setRows(Array.isArray(data) ? data : []);
        } catch (loadError) {
            console.error("Ошибка загрузки отчёта наценок:", loadError);
            setError(loadError?.response?.data?.detail || "Не удалось загрузить отчёт наценок.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
    }, []);

    const filteredRows = useMemo(() => {
        const selectedSupplierCode = String(supplierFilter || "").trim();
        const selectedStoreId = String(storeFilter || "").trim();
        return rows.filter((row) => {
            if (selectedSupplierCode && String(row.supplier_code || "").trim() !== selectedSupplierCode) {
                return false;
            }
            if (selectedStoreId && String(row.store_id || "").trim() !== selectedStoreId) {
                return false;
            }
            if (showOnlyMissing && !row.is_required_missing) {
                return false;
            }
            return true;
        });
    }, [rows, showOnlyMissing, storeFilter, supplierFilter]);

    const supplierFilterOptions = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            const code = String(row.supplier_code || "").trim();
            if (!code || map.has(code)) {
                return;
            }
            map.set(code, {
                value: code,
                label: row.supplier_display_name || `${row.supplier_name || "Поставщик"} (${code})`,
            });
        });
        return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [rows]);

    const storeFilterOptions = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            const storeId = String(row.store_id || "").trim();
            if (!storeId || map.has(storeId)) {
                return;
            }
            const details = [
                row.store_code,
                row.enterprise_code,
                row.tabletki_branch ? `Branch ${row.tabletki_branch}` : null,
            ].filter(Boolean);
            map.set(storeId, {
                value: storeId,
                label: `${row.store_name || row.store_code || "Магазин"}${details.length ? ` · ${details.join(" · ")}` : ""}`,
            });
        });
        return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [rows]);

    const missingCount = rows.filter((row) => row.is_required_missing).length;

    const handleRowOpen = (row) => {
        if (onOpenStoreSettings) {
            onOpenStoreSettings({ supplierCode: row.supplier_code, storeId: row.store_id });
        }
    };

    return (
        <div className="sp-section">
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Наценки</h2>
                    </div>
                    <div className="sp-toolbar-actions">
                        <button type="button" className="sp-btn sp-btn-ghost" disabled={loading} onClick={loadReport}>
                            Обновить
                        </button>
                    </div>
                </div>

                <div className="sp-filters">
                    <SpSelect
                        label="Фильтр по поставщику"
                        value={supplierFilter}
                        onChange={setSupplierFilter}
                        placeholder="Не выбрано"
                        options={supplierFilterOptions}
                    />
                    <SpSelect
                        label="Фильтр по магазину"
                        value={storeFilter}
                        onChange={setStoreFilter}
                        placeholder="Не выбрано"
                        options={storeFilterOptions}
                    />
                    <SpCheck
                        label="Только без обязательной надбавки"
                        checked={showOnlyMissing}
                        onChange={setShowOnlyMissing}
                    />
                </div>

                <div className="sp-stats">
                    <span className="sp-stat">Строк <span>{filteredRows.length}</span></span>
                    <span className={missingCount > 0 ? "sp-stat warn" : "sp-stat zero"}>
                        Без надбавки <span>{missingCount}</span>
                    </span>
                </div>

                {loading ? <div className="sp-mini">Загрузка отчёта наценок…</div> : null}
                {error ? <div className="sp-msg err">{error}</div> : null}
            </div>

            <div className="sp-card" style={{ padding: "16px 18px" }}>
                <div className="sp-tbl-wrap">
                    <table className="sp-tbl">
                        <thead>
                            <tr>
                                <th>Поставщик</th>
                                <th>Магазин</th>
                                <th>Минимальная ценовая надбавка</th>
                                <th>Глобальный fallback</th>
                                <th>RRP</th>
                                <th>Расчёт opt (%)</th>
                                <th>Доп. наценка</th>
                                <th>Обновлено</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.map((row) => (
                                <tr
                                    key={`${row.supplier_code}-${row.store_id}-${row.settings_id}`}
                                    className="sp-click"
                                    role="button"
                                    tabIndex={0}
                                    title="Открыть настройки пары поставщик–магазин"
                                    onClick={() => handleRowOpen(row)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleRowOpen(row);
                                        }
                                    }}
                                >
                                    <td>
                                        <div style={{ fontWeight: 700 }}>{row.supplier_display_name || row.supplier_name}</div>
                                        <div className="sp-mini">Код: {row.supplier_code}</div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{row.store_name || row.store_code}</div>
                                        <div className="sp-mini">
                                            {[row.store_code, row.enterprise_code, row.tabletki_branch ? `Branch ${row.tabletki_branch}` : null]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </div>
                                    </td>
                                    <td>
                                        {row.is_required_missing ? (
                                            <span className="sp-pill warn">Обязательно заполнить</span>
                                        ) : (
                                            <span className="sp-strong">{formatAmount(row.effective_min_markup_threshold)}</span>
                                        )}
                                        {row.uses_supplier_fallback ? (
                                            <div className="sp-mini" style={{ marginTop: "4px" }}>Используется fallback поставщика</div>
                                        ) : null}
                                    </td>
                                    <td>{formatAmount(row.supplier_min_markup_threshold)}</td>
                                    <td>{boolPill(row.is_rrp)}</td>
                                    <td>{row.price_opt_calc_percent ?? "fallback"}</td>
                                    <td>
                                        {row.extra_markup_enabled ? (
                                            <span>{`${row.extra_markup_mode || "percent"} / ${row.extra_markup_value ?? "диапазон"}`}</span>
                                        ) : (
                                            <span className="sp-pill off">Выключена</span>
                                        )}
                                    </td>
                                    <td className="sp-mini">{row.updated_at ? row.updated_at.slice(0, 10) : "—"}</td>
                                    <td className="sp-go" aria-hidden="true">›</td>
                                </tr>
                            ))}
                            {!loading && filteredRows.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="sp-mini">Активные пары поставщик-магазин не найдены.</td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const SupplierReconciliationPanel = () => {
    const [options, setOptions] = useState({ suppliers: [], business_entities: [], deposit_groups: [] });
    const [period, setPeriod] = useState(defaultMonthPeriod);
    const [supplierCode, setSupplierCode] = useState("");
    // "" — все юрлица, "entity:<id>" — одно юрлицо, "group:<id>" — все юрлица депозитной группы (один акт на группу)
    const [scopeValue, setScopeValue] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [ordersFile, setOrdersFile] = useState(null);
    const [balanceFile, setBalanceFile] = useState(null);
    const [dropshipFile, setDropshipFile] = useState(null);
    const [usdUahRate, setUsdUahRate] = useState("");
    const [loading, setLoading] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [error, setError] = useState("");
    const [downloadError, setDownloadError] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [result, setResult] = useState(null);
    const periodOptions = useMemo(() => buildMonthPeriodOptions(), []);

    const loadOptions = async () => {
        setOptionsLoading(true);
        setError("");
        try {
            const data = await getSupplierReconciliationOptions();
            const nextOptions = {
                suppliers: Array.isArray(data?.suppliers) ? data.suppliers : [],
                business_entities: Array.isArray(data?.business_entities) ? data.business_entities : [],
                deposit_groups: Array.isArray(data?.deposit_groups) ? data.deposit_groups : [],
            };
            setOptions(nextOptions);
            if (!supplierCode) {
                const preferred = nextOptions.suppliers.find((item) => item.supported) || nextOptions.suppliers[0];
                if (preferred?.code) {
                    setSupplierCode(preferred.code);
                }
            }
        } catch (loadError) {
            console.error("Ошибка загрузки настроек сверки:", loadError);
            setError("Не удалось загрузить список поставщиков и юрлиц для сверки.");
        } finally {
            setOptionsLoading(false);
        }
    };

    useEffect(() => {
        loadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedSupplier = useMemo(
        () => options.suppliers.find((item) => String(item.code) === String(supplierCode)) || null,
        [options.suppliers, supplierCode],
    );

    const supplierDepositGroups = useMemo(
        () => options.deposit_groups.filter((group) => (group.supplier_codes || []).map(String).includes(String(supplierCode))),
        [options.deposit_groups, supplierCode],
    );
    const [scopeKind, scopeId] = scopeValue ? scopeValue.split(":") : ["", ""];
    const businessEntityId = scopeKind === "entity" ? scopeId : "";
    const depositGroupId = scopeKind === "group" ? scopeId : "";

    useEffect(() => {
        if (depositGroupId && !supplierDepositGroups.some((group) => String(group.id) === String(depositGroupId))) {
            setScopeValue("");
        }
    }, [depositGroupId, supplierDepositGroups]);

    const scopeOptions = useMemo(
        () => [
            ...supplierDepositGroups.map((group) => ({
                value: `group:${group.id}`,
                label: `Группа · ${group.name} (${(group.entities || []).map((entity) => entity.short_name).join(", ")})`,
                group: "Депозитные группы (один акт на группу)",
            })),
            ...options.business_entities.map((entity) => ({
                value: `entity:${entity.id}`,
                label: `${entity.short_name}${entity.tax_id ? ` · ${entity.tax_id}` : ""}`,
                group: supplierDepositGroups.length ? "Отдельное юрлицо" : null,
            })),
        ],
        [supplierDepositGroups, options.business_entities],
    );

    const selectedEntityLabel = useMemo(() => {
        if (!scopeValue) {
            return "Все юрлица";
        }
        if (depositGroupId) {
            const group = supplierDepositGroups.find((item) => String(item.id) === String(depositGroupId));
            return group ? `Группа «${group.name}»` : "Депозитная группа";
        }
        const entity = options.business_entities.find((item) => String(item.id) === String(businessEntityId));
        return entity?.short_name || "Выбранное юрлицо";
    }, [scopeValue, depositGroupId, businessEntityId, supplierDepositGroups, options.business_entities]);
    const selectedProvider = selectedSupplier?.reconciliation_provider || "";
    const reconciliationCapabilities = selectedSupplier?.reconciliation_capabilities || {};
    const fileAccept = reconciliationCapabilities.file_accept || (["biotus", "dsn", "sportatlet"].includes(selectedProvider) ? ".xls,.xlsx" : selectedProvider === "monsterlab" ? ".xls,.xlsx,.pdf" : ".xls,.xlsx,.pdf");
    const isProteinPlus = selectedProvider === "proteinplus";
    // Sport-atlet: второй файл «Дропшипінг» (ТТН, отказы) рядом с актом.
    const secondaryFile = reconciliationCapabilities.secondary_file || null;
    const hidesPaymentsAndDeposit = Boolean(reconciliationCapabilities.hides_payments_and_deposit || selectedProvider === "dobavki");

    const handleRun = async () => {
        setError("");
        setDownloadError("");
        setResult(null);
        if (!period) {
            setError("Выберите месяц сверки.");
            return;
        }
        if (!supplierCode) {
            setError("Выберите поставщика.");
            return;
        }
        if (isProteinPlus) {
            if (!ordersFile || !balanceFile) {
                setError("Для ProteinPlus загрузите файл заказов с ТТН и файл баланса.");
                return;
            }
            if (usdUahRate && Number(usdUahRate) <= 0) {
                setError("Курс USD→UAH должен быть больше нуля.");
                return;
            }
        } else {
            if (!selectedFile) {
                setError("Загрузите акт сверки поставщика.");
                return;
            }
            if (secondaryFile?.required && !dropshipFile) {
                setError(`Загрузите файл «${secondaryFile.label}».`);
                return;
            }
        }
        setLoading(true);
        try {
            const data = await runSupplierReconciliation({
                period,
                supplierCode,
                businessEntityId,
                depositGroupId,
                file: selectedFile,
                ordersFile,
                balanceFile,
                dropshipFile: secondaryFile ? dropshipFile : null,
                usdUahRate,
            });
            setResult(data);
        } catch (runError) {
            console.error("Ошибка сверки поставщика:", runError);
            const detail = runError?.response?.data?.detail;
            setError(typeof detail === "string" && detail ? detail : "Не удалось выполнить сверку.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloadError("");
        if (!result?.download_url) {
            setDownloadError("Файл деталей пока не сформирован.");
            return;
        }
        setDownloading(true);
        try {
            await downloadSupplierReconciliationReport(result.download_url, result.report_filename || "supplier_reconciliation.xlsx");
        } catch (downloadProblem) {
            console.error("Ошибка скачивания деталей сверки:", downloadProblem);
            const detail = downloadProblem?.response?.data?.detail;
            setDownloadError(typeof detail === "string" && detail ? detail : "Не удалось скачать Excel с деталями.");
        } finally {
            setDownloading(false);
        }
    };

    const summaryCards = [
        ["payments", "Оплаты"],
        ["orders", "Заказы"],
        ["returns", "Возвраты"],
    ].filter(([key]) => !(hidesPaymentsAndDeposit && key === "payments"));

    return (
        <div className="sp-section">
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Сверка поставщика</h2>
                        <p>Biotus, DSN, Sport-atlet (акт + дропшипінг), Dobavki.ua, MonsterLab, ProteinPlus и Vitaworld: сверка за выбранный месяц.</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={loadOptions} disabled={optionsLoading}>
                            Обновить списки
                        </button>
                    </div>
                </div>

                <div className="sp-form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    <SpSelect
                        label="Период"
                        value={period}
                        onChange={setPeriod}
                        placeholder={null}
                        options={periodOptions}
                    />
                    <SpSelect
                        label="Поставщик"
                        value={supplierCode}
                        onChange={setSupplierCode}
                        disabled={optionsLoading}
                        placeholder={null}
                        options={options.suppliers.map((supplier) => ({
                            value: supplier.code,
                            label: `${supplier.name || supplier.code} (${supplier.code})${supplier.supported ? "" : " · позже"}`,
                        }))}
                    />
                    <SpSelect
                        label="Юрлицо"
                        value={scopeValue}
                        onChange={setScopeValue}
                        disabled={optionsLoading}
                        placeholder="Все юрлица"
                        options={scopeOptions}
                    />
                    {isProteinPlus ? (
                        <>
                            <div className="sp-fld">
                                <label>Заказы с ТТН</label>
                                <input
                                    className="sp-inp"
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={(event) => setOrdersFile(event.target.files?.[0] || null)}
                                />
                            </div>
                            <div className="sp-fld">
                                <label>Баланс и возвраты</label>
                                <input
                                    className="sp-inp"
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={(event) => setBalanceFile(event.target.files?.[0] || null)}
                                />
                            </div>
                            <SpInput
                                label="Курс USD→UAH (необязательно)"
                                type="number"
                                value={usdUahRate}
                                placeholder="авто"
                                onChange={setUsdUahRate}
                            />
                        </>
                    ) : (
                        <div className="sp-fld">
                            <label>{reconciliationCapabilities.file_label || "Акт сверки"}</label>
                            <input
                                className="sp-inp"
                                type="file"
                                accept={fileAccept}
                                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                            />
                        </div>
                    )}
                    {!isProteinPlus && secondaryFile ? (
                        <div className="sp-fld">
                            <label>{secondaryFile.label}{secondaryFile.required ? "" : " (необязательно)"}</label>
                            <input
                                className="sp-inp"
                                type="file"
                                accept={secondaryFile.accept || ".xls,.xlsx"}
                                onChange={(event) => setDropshipFile(event.target.files?.[0] || null)}
                            />
                        </div>
                    ) : null}
                </div>

                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                    <button type="button" className="sp-btn sp-btn-primary" onClick={handleRun} disabled={loading || optionsLoading}>
                        {loading ? "Сверяю…" : "Сверить"}
                    </button>
                    <span className="sp-mini">
                        {selectedSupplier ? `${selectedSupplier.name || selectedSupplier.code} · ${selectedEntityLabel}` : selectedEntityLabel}
                    </span>
                </div>

                {error ? <div className="sp-msg err">{error}</div> : null}
            </div>

            {result ? (
                <div className="sp-card" style={{ padding: "16px 18px", display: "grid", gap: "16px" }}>
                    <div className="sp-toolbar-head">
                        <div>
                            <h2 className="sp-h2">Результат сверки</h2>
                            <div className="sp-hint" style={{ marginTop: "4px" }}>
                                {result.period} · {result.supplier_name || result.supplier_code} · {result.business_entity_name || "Все юрлица"}
                            </div>
                        </div>
                        {result.download_url ? (
                            <button type="button" onClick={handleDownload} disabled={downloading} className="sp-btn sp-btn-primary">
                                {downloading ? "Скачиваю…" : "Скачать детали.xlsx"}
                            </button>
                        ) : null}
                    </div>
                    {downloadError ? <div className="sp-msg err">{downloadError}</div> : null}
                    {Array.isArray(result.warnings) && result.warnings.length > 0 ? (
                        <div className="sp-warnbox">
                            {result.warnings.map((warning, index) => (
                                <div key={`${warning}-${index}`}>{warning}</div>
                            ))}
                        </div>
                    ) : null}

                    <div className="sp-result-grid">
                        {result.deposit && !result.hides_payments_and_deposit ? (
                            <div className="sp-result-card">
                                <div className="rc-title">Депозит</div>
                                <div className="rc-lines">
                                    <div>Начало база: {formatAmount(result.deposit.base_opening_amount)}</div>
                                    <div>Начало акт: {formatAmount(result.deposit.act_opening_amount)}</div>
                                    <div className={Number(result.deposit.opening_delta || 0) === 0 ? "sp-delta good" : "sp-delta bad"}>
                                        Расхождение начало: {formatAmount(result.deposit.opening_delta)}
                                    </div>
                                    <div>Конец база: {formatAmount(result.deposit.base_closing_amount)}</div>
                                    <div>Конец акт: {formatAmount(result.deposit.act_closing_amount)}</div>
                                    <div className={Number(result.deposit.closing_delta || 0) === 0 ? "sp-delta good" : "sp-delta bad"}>
                                        Расхождение конец: {formatAmount(result.deposit.closing_delta)}
                                    </div>
                                    <div className="muted">
                                        Группы: {(result.deposit.group_names || []).join(", ") || "не найдены"}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {result.bonus_amount !== null && result.bonus_amount !== undefined ? (
                            <div className="sp-result-card">
                                <div className="rc-title">Dobavki.ua</div>
                                <div className="rc-lines">
                                    <div>Сумма бонуса: {formatAmount(result.bonus_amount)}</div>
                                    <div>Транзитные: {result.transit_count ?? 0} / {formatAmount(result.transit_amount)}</div>
                                </div>
                            </div>
                        ) : null}
                        {summaryCards.map(([key, label]) => {
                            const summary = result.summaries?.[key] || {};
                            const delta = Number(summary.amount_delta || 0);
                            const hideActTotals = Boolean(result.hides_payments_and_deposit);
                            return (
                                <div key={key} className="sp-result-card">
                                    <div className="rc-title">{label}</div>
                                    <div className="rc-lines">
                                        <div>База за период: {summary.base_count ?? 0} / {formatAmount(summary.base_amount)}</div>
                                        {!hideActTotals ? <div>Акт: {summary.act_count ?? 0} / {formatAmount(summary.act_amount)}</div> : null}
                                        {!hideActTotals ? (
                                            <div className={delta === 0 ? "sp-delta good" : "sp-delta bad"}>
                                                Дельта: {summary.count_delta ?? 0} / {formatAmount(summary.amount_delta)}
                                            </div>
                                        ) : null}
                                        <div className="muted">
                                            Совпало: {summary.matched_count ?? 0}{hideActTotals ? "" : ` / база ${formatAmount(summary.matched_base_amount)} / акт ${formatAmount(summary.matched_act_amount)}`}
                                        </div>
                                        {!hideActTotals ? <div className="muted">Дельта совпавших: {formatAmount(summary.matched_amount_delta)}</div> : null}
                                        <div className="muted">Только в базе: {summary.only_base_count ?? 0} / {formatAmount(summary.only_base_amount)}</div>
                                        <div className="muted">Только в файле: {summary.only_act_count ?? 0}{hideActTotals ? "" : ` / ${formatAmount(summary.only_act_amount)}`}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const SuppliersPage = () => {
    const [activeSection, setActiveSection] = useState("dropship");
    const [suppliers, setSuppliers] = useState([]);
    const [selectedCode, setSelectedCode] = useState("");
    const [detail, setDetail] = useState(null);
    const [draft, setDraft] = useState(defaultDraft);
    const [isCreating, setIsCreating] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [listError, setListError] = useState("");
    const [detailError, setDetailError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState("");
    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const [senderQuery, setSenderQuery] = useState("");
    const [activeCardTab, setActiveCardTab] = useState("store");
    const [ordersExportDate, setOrdersExportDate] = useState(() => previousDayValue());
    const [ordersExportLoading, setOrdersExportLoading] = useState(false);
    const [ordersExportError, setOrdersExportError] = useState("");
    const [ordersExportMessage, setOrdersExportMessage] = useState("");
    const [businessStores, setBusinessStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(true);
    const [storesError, setStoresError] = useState("");
    const [selectedStoreEnterpriseCode, setSelectedStoreEnterpriseCode] = useState("");
    const [selectedStoreId, setSelectedStoreId] = useState("");
    const [storeSettingsDraft, setStoreSettingsDraft] = useState(defaultStoreSettingsDraft);
    const [storeSettingsOverview, setStoreSettingsOverview] = useState([]);
    const [storeSettingsLoading, setStoreSettingsLoading] = useState(false);
    const [storeSettingsError, setStoreSettingsError] = useState("");
    const [storeSettingsSaveError, setStoreSettingsSaveError] = useState("");
    const [storeSettingsSaveSuccess, setStoreSettingsSaveSuccess] = useState("");
    const [counterparties, setCounterparties] = useState([]);
    const [selectedCounterpartyId, setSelectedCounterpartyId] = useState("");
    const [counterpartyError, setCounterpartyError] = useState("");
    const [counterpartySuccess, setCounterpartySuccess] = useState("");
    const storeSettingsRequestRef = useRef(0);
    // Deep-link target store set from the «Наценки» tab: consumed by the
    // store-settings overview loader so the right store opens preselected.
    const pendingStoreIdRef = useRef("");

    const loadList = async () => {
        setListLoading(true);
        setListError("");
        try {
            const data = await getSuppliersViewList();
            setSuppliers(data);
            if (!selectedCode && data.length > 0 && !isCreating) {
                setSelectedCode(data[0].code);
            }
        } catch (error) {
            console.error("Ошибка загрузки списка поставщиков:", error);
            setListError("Не удалось загрузить список поставщиков.");
        } finally {
            setListLoading(false);
        }
    };

    const loadBusinessStores = async () => {
        setStoresLoading(true);
        setStoresError("");
        try {
            const data = await getBusinessStores();
            setBusinessStores(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Ошибка загрузки business stores:", error);
            setStoresError("Не удалось загрузить список магазинов Business-контура.");
        } finally {
            setStoresLoading(false);
        }
    };

    const loadCounterparties = async () => {
        setCounterpartyError("");
        try {
            const data = await listCounterparties({ limit: 1000 });
            setCounterparties(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Ошибка загрузки контрагентов:", error);
            setCounterpartyError("Не удалось загрузить контрагентов.");
        }
    };

    useEffect(() => {
        loadList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreating, selectedCode]);

    useEffect(() => {
        loadBusinessStores();
        loadCounterparties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedCode || isCreating) {
            setStoreSettingsOverview([]);
            setSelectedStoreEnterpriseCode("");
            setSelectedStoreId("");
            setStoreSettingsDraft(defaultStoreSettingsDraft);
            storeSettingsRequestRef.current += 1;
            return;
        }

        const loadDetail = async () => {
            setDetailLoading(true);
            setDetailError("");
            try {
                const data = await getSupplierViewDetail(selectedCode);
                setDetail(data);
                setDraft({
                    is_active: Boolean(data.is_active),
                    code: data.code || "",
                    name: data.name || "",
                    city: data.cities_raw || "",
                    salesdrive_supplier_id: data.salesdrive_supplier_id ?? "",
                    biotus_orders_enabled: Boolean(data.biotus_orders_enabled),
                    np_fulfillment_enabled: Boolean(data.np_fulfillment_enabled),
                    schedule_enabled: Boolean(data.schedule_enabled),
                    block_start_day: data.block_start_day != null ? String(data.block_start_day) : "",
                    block_start_time: data.block_start_time || "",
                    block_end_day: data.block_end_day != null ? String(data.block_end_day) : "",
                    block_end_time: data.block_end_time || "",
                    shipping_schedule: normalizeShippingSchedule(data.shipping_schedule),
                    feed_url: data.feed_url || "",
                    gdrive_folder: data.gdrive_folder || "",
                    is_rrp: Boolean(data.is_rrp),
                    profit_percent: data.profit_percent ?? "",
                    retail_markup: data.retail_markup ?? "",
                    min_markup_threshold: data.min_markup_threshold ?? "",
                    priority: data.priority ?? 5,
                    publish_priority_tier: data.publish_priority_tier ?? 0,
                    use_feed_instead_of_gdrive: Boolean(data.use_feed_instead_of_gdrive),
                    order_ttn_mode: data.order_ttn_mode || "auto",
                });
            } catch (error) {
                console.error("Ошибка загрузки поставщика:", error);
                setDetail(null);
                setDetailError("Не удалось загрузить карточку поставщика.");
            } finally {
                setDetailLoading(false);
            }
        };

        loadDetail();
    }, [isCreating, selectedCode]);

    useEffect(() => {
        if (!selectedCode || isCreating) {
            return;
        }

        const loadOverview = async () => {
            setStoreSettingsLoading(true);
            setStoreSettingsError("");
            setStoreSettingsSaveError("");
            setStoreSettingsSaveSuccess("");
            try {
                const overview = await getBusinessSupplierStoreSettingsOverview(selectedCode);
                setStoreSettingsOverview(Array.isArray(overview) ? overview : []);

                const pendingStoreId = String(pendingStoreIdRef.current || "").trim();
                pendingStoreIdRef.current = "";
                const preferredStoreId =
                    pendingStoreId
                    || String(selectedStoreId || "").trim()
                    || String((Array.isArray(overview) && overview[0]?.store_id) || "")
                    || String((businessStores[0] || {}).id || "");

                if (preferredStoreId) {
                    const preferredStore = businessStores.find((item) => String(item.id) === preferredStoreId);
                    setSelectedStoreId(preferredStoreId);
                    setSelectedStoreEnterpriseCode(String(preferredStore?.enterprise_code || ""));
                    const exact = Array.isArray(overview)
                        ? overview.find((item) => String(item.store_id) === preferredStoreId)
                        : null;
                    await applyStoreSettingsDraft(preferredStoreId, selectedCode, exact || null);
                } else {
                    setSelectedStoreEnterpriseCode("");
                    setSelectedStoreId("");
                    setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, null));
                }
            } catch (error) {
                console.error("Ошибка загрузки обзора настроек поставщика по магазинам:", error);
                setStoreSettingsOverview([]);
                setStoreSettingsError("Не удалось загрузить настройки поставщика по магазинам.");
                setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, null));
            } finally {
                setStoreSettingsLoading(false);
            }
        };

        loadOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreating, selectedCode, businessStores]);

    const selectedListItem = useMemo(
        () => suppliers.find((item) => item.code === selectedCode) || null,
        [suppliers, selectedCode]
    );
    const filteredSuppliers = useMemo(() => {
        const base = showOnlyActive ? suppliers.filter((item) => item.is_active) : suppliers;
        const query = String(senderQuery || "").trim().toLowerCase();
        if (!query) {
            return base;
        }
        return base.filter((item) => {
            const code = String(item.code || "").toLowerCase();
            const name = String(item.display_name || "").toLowerCase();
            return code.includes(query) || name.includes(query);
        });
    }, [showOnlyActive, suppliers, senderQuery]);
    const businessStoreEnterpriseOptions = useMemo(() => {
        const seen = new Set();
        const rows = [];

        businessStores.forEach((store) => {
            const enterpriseCode = String(store?.enterprise_code || "").trim();
            if (!enterpriseCode || seen.has(enterpriseCode)) {
                return;
            }
            seen.add(enterpriseCode);
            rows.push({
                enterprise_code: enterpriseCode,
                label: `Предприятие ${enterpriseCode}`,
            });
        });

        return rows.sort((left, right) => left.label.localeCompare(right.label));
    }, [businessStores]);
    const filteredBusinessStores = useMemo(() => {
        const normalizedEnterpriseCode = String(selectedStoreEnterpriseCode || "").trim();
        if (!normalizedEnterpriseCode) {
            return businessStores;
        }
        return businessStores.filter(
            (store) => String(store?.enterprise_code || "").trim() === normalizedEnterpriseCode,
        );
    }, [businessStores, selectedStoreEnterpriseCode]);
    const showEditor = isCreating || Boolean(detail);
    const overviewByStoreId = useMemo(() => {
        const map = new Map();
        storeSettingsOverview.forEach((item) => {
            map.set(String(item.store_id), item);
        });
        return map;
    }, [storeSettingsOverview]);
    const linkedCounterparties = useMemo(
        () => counterparties
            .map((counterparty) => {
                const link = (counterparty.dropship_links || []).find(
                    (item) => String(item.supplier_code || "").trim() === String(selectedCode || "").trim(),
                );
                return link ? { counterparty, link } : null;
            })
            .filter(Boolean),
        [counterparties, selectedCode],
    );
    const unlinkedCounterparties = useMemo(
        () => counterparties.filter(
            (counterparty) => !(counterparty.dropship_links || []).some(
                (item) => String(item.supplier_code || "").trim() === String(selectedCode || "").trim(),
            ),
        ),
        [counterparties, selectedCode],
    );
    const cardTitle = isCreating ? "Новый отправитель" : "Карточка отправителя";

    const setField = (key, value) => {
        setDraft((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const setShippingScheduleDay = (weekday, key, value) => {
        setDraft((prev) => ({
            ...prev,
            shipping_schedule: normalizeShippingSchedule(prev.shipping_schedule).map((item) => {
                if (item.weekday !== weekday) {
                    return item;
                }
                if (key === "mode" && value !== "scheduled") {
                    return { ...item, mode: value, order_cutoff_time: "", shipping_time: "" };
                }
                return { ...item, [key]: value };
            }),
        }));
    };

    const resetCreate = () => {
        setIsCreating(true);
        setSelectedCode("");
        setDetail(null);
        setDetailError("");
        setSaveError("");
        setSaveSuccess("");
        setSelectedStoreEnterpriseCode("");
        setDraft(defaultDraft);
        setSelectedStoreId("");
        setStoreSettingsOverview([]);
        setStoreSettingsDraft(defaultStoreSettingsDraft);
        setStoreSettingsError("");
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");
        setActiveCardTab("store");
    };

    const selectSupplier = (code) => {
        setIsCreating(false);
        setSaveError("");
        setSaveSuccess("");
        setActiveCardTab("store");
        setStoreSettingsError("");
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");
        setSelectedStoreEnterpriseCode("");
        setSelectedStoreId("");
        setStoreSettingsOverview([]);
        setStoreSettingsDraft(defaultStoreSettingsDraft);
        storeSettingsRequestRef.current += 1;
        setSelectedCode(code);
    };

    // Deep-link from the «Наценки» report row → open the supplier card on the
    // «По магазину» tab with the target store preselected.
    const openStoreSettings = ({ supplierCode, storeId }) => {
        const code = String(supplierCode || "").trim();
        const targetStoreId = String(storeId || "").trim();
        if (!code) {
            return;
        }
        pendingStoreIdRef.current = targetStoreId;
        setActiveSection("dropship");
        setActiveCardTab("store");
        if (!isCreating && String(selectedCode) === code) {
            // Supplier already loaded — no overview reload will fire, so select
            // the store directly (pending ref left set is harmless, cleared here).
            pendingStoreIdRef.current = "";
            if (targetStoreId) {
                handleStoreSelection(targetStoreId);
            }
            return;
        }
        selectSupplier(code);
    };

    const handleCancel = () => {
        setSaveError("");
        setSaveSuccess("");
        setStoreSettingsError("");
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");
        if (isCreating) {
            setIsCreating(false);
            if (suppliers.length > 0) {
                setSelectedCode(suppliers[0].code);
            }
            return;
        }

        if (detail) {
            setDraft({
                is_active: Boolean(detail.is_active),
                code: detail.code || "",
                name: detail.name || "",
                city: detail.cities_raw || "",
                salesdrive_supplier_id: detail.salesdrive_supplier_id ?? "",
                biotus_orders_enabled: Boolean(detail.biotus_orders_enabled),
                np_fulfillment_enabled: Boolean(detail.np_fulfillment_enabled),
                schedule_enabled: Boolean(detail.schedule_enabled),
                block_start_day: detail.block_start_day != null ? String(detail.block_start_day) : "",
                block_start_time: detail.block_start_time || "",
                block_end_day: detail.block_end_day != null ? String(detail.block_end_day) : "",
                block_end_time: detail.block_end_time || "",
                shipping_schedule: normalizeShippingSchedule(detail.shipping_schedule),
                feed_url: detail.feed_url || "",
                gdrive_folder: detail.gdrive_folder || "",
                is_rrp: Boolean(detail.is_rrp),
                profit_percent: detail.profit_percent ?? "",
                retail_markup: detail.retail_markup ?? "",
                min_markup_threshold: detail.min_markup_threshold ?? "",
                priority: detail.priority ?? 5,
                publish_priority_tier: detail.publish_priority_tier ?? 0,
                use_feed_instead_of_gdrive: Boolean(detail.use_feed_instead_of_gdrive),
                order_ttn_mode: detail.order_ttn_mode || "auto",
            });
            setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, overviewByStoreId.get(String(selectedStoreId)) || null));
        }
    };

    const buildStoreSettingsDraft = (supplierCode, existing) => {
        const defaultMinMarkupThreshold = detail?.min_markup_threshold ?? "0";

        return {
            supplier_code: String(supplierCode || ""),
            is_active: existing ? Boolean(existing.is_active) : true,
            is_rrp: existing ? Boolean(existing.is_rrp) : false,
            priority_override: existing?.priority_override ?? "",
            min_markup_threshold: existing?.min_markup_threshold ?? defaultMinMarkupThreshold,
            price_opt_calc_percent: existing?.price_opt_calc_percent ?? "",
            extra_markup_enabled: existing ? Boolean(existing.extra_markup_enabled) : false,
            extra_markup_mode: String(existing?.extra_markup_mode || "percent"),
            extra_markup_value: existing?.extra_markup_value ?? "",
            extra_markup_min: existing?.extra_markup_min ?? "",
            extra_markup_max: existing?.extra_markup_max ?? "",
            dumping_mode: existing ? Boolean(existing.dumping_mode) : false,
        };
    };

    const applyStoreSettingsDraft = async (storeId, supplierCode, prefetchedOverview = null) => {
        const requestId = storeSettingsRequestRef.current + 1;
        storeSettingsRequestRef.current = requestId;
        const existingFromOverview = prefetchedOverview ?? overviewByStoreId.get(String(storeId));
        if (existingFromOverview) {
            if (storeSettingsRequestRef.current === requestId) {
                setStoreSettingsDraft(buildStoreSettingsDraft(supplierCode, existingFromOverview));
            }
            return;
        }

        try {
            const rows = await getBusinessStoreSupplierSettings(storeId);
            if (storeSettingsRequestRef.current !== requestId) {
                return;
            }
            const exact = Array.isArray(rows)
                ? rows.find((item) => String(item.supplier_code || "").trim() === String(supplierCode || "").trim())
                : null;
            setStoreSettingsDraft(buildStoreSettingsDraft(supplierCode, exact || null));
        } catch (error) {
            if (storeSettingsRequestRef.current !== requestId) {
                return;
            }
            console.error("Ошибка загрузки store-specific supplier settings:", error);
            setStoreSettingsDraft(buildStoreSettingsDraft(supplierCode, null));
            setStoreSettingsError("Не удалось загрузить настройки поставщика для выбранного магазина.");
        }
    };

    const buildPayload = () => ({
        code: String(draft.code || "").trim(),
        name: String(draft.name || "").trim(),
        city: String(draft.city || "").trim() || null,
        salesdrive_supplier_id:
            draft.salesdrive_supplier_id === "" ? null : Number(draft.salesdrive_supplier_id),
        biotus_orders_enabled: Boolean(draft.biotus_orders_enabled),
        np_fulfillment_enabled: Boolean(draft.np_fulfillment_enabled),
        schedule_enabled: Boolean(draft.schedule_enabled),
        block_start_day: draft.block_start_day === "" ? null : Number(draft.block_start_day),
        block_start_time: String(draft.block_start_time || "").trim() || null,
        block_end_day: draft.block_end_day === "" ? null : Number(draft.block_end_day),
        block_end_time: String(draft.block_end_time || "").trim() || null,
        shipping_schedule: normalizeShippingSchedule(draft.shipping_schedule).map((item) => ({
            weekday: item.weekday,
            mode: item.mode,
            order_cutoff_time: item.mode === "scheduled" ? String(item.order_cutoff_time || "").trim() || null : null,
            shipping_time: item.mode === "scheduled" ? String(item.shipping_time || "").trim() || null : null,
        })),
        feed_url: String(draft.feed_url || "").trim() || null,
        gdrive_folder: String(draft.gdrive_folder || "").trim() || null,
        is_rrp: Boolean(draft.is_rrp),
        profit_percent: draft.profit_percent === "" ? null : Number(draft.profit_percent),
        retail_markup: draft.retail_markup === "" ? null : Number(draft.retail_markup),
        min_markup_threshold: draft.min_markup_threshold === "" ? null : Number(draft.min_markup_threshold),
        is_active: Boolean(draft.is_active),
        priority: draft.priority === "" ? 5 : Number(draft.priority),
        publish_priority_tier: draft.publish_priority_tier === "" ? 0 : Number(draft.publish_priority_tier),
        use_feed_instead_of_gdrive: Boolean(draft.use_feed_instead_of_gdrive),
        order_ttn_mode: draft.order_ttn_mode === "manual_later" ? "manual_later" : "auto",
    });

    const setStoreSettingsField = (key, value) => {
        setStoreSettingsDraft((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleStoreSelection = async (value, options = {}) => {
        const { syncEnterprise = true } = options;
        const selectedStore = businessStores.find((item) => String(item.id) === String(value));
        if (syncEnterprise && selectedStore?.enterprise_code) {
            setSelectedStoreEnterpriseCode(String(selectedStore.enterprise_code));
        }
        setSelectedStoreId(value);
        setStoreSettingsError("");
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");
        if (!value || !selectedCode) {
            setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, null));
            return;
        }
        await applyStoreSettingsDraft(value, selectedCode);
    };

    const handleStoreEnterpriseSelection = (enterpriseCode) => {
        setSelectedStoreEnterpriseCode(enterpriseCode);
        setSelectedStoreId("");
        setStoreSettingsError("");
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");

        const normalizedEnterpriseCode = String(enterpriseCode || "").trim();
        if (!normalizedEnterpriseCode) {
            setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, null));
            storeSettingsRequestRef.current += 1;
            return;
        }

        setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, null));
        storeSettingsRequestRef.current += 1;
    };

    const buildStoreSettingsPayload = () => {
        const minMarkupThreshold = String(storeSettingsDraft.min_markup_threshold ?? "").trim();
        if (!minMarkupThreshold) {
            throw new Error("Минимальная ценовая надбавка обязательна.");
        }

        return {
            supplier_code: String(selectedCode || "").trim(),
            is_active: Boolean(storeSettingsDraft.is_active),
            is_rrp: Boolean(storeSettingsDraft.is_rrp),
            priority_override:
                String(storeSettingsDraft.priority_override ?? "").trim() === ""
                    ? null
                    : Number(storeSettingsDraft.priority_override),
            min_markup_threshold: Number(minMarkupThreshold),
            price_opt_calc_percent:
                String(storeSettingsDraft.price_opt_calc_percent ?? "").trim() === ""
                    ? null
                    : Number(storeSettingsDraft.price_opt_calc_percent),
            extra_markup_enabled: Boolean(storeSettingsDraft.extra_markup_enabled),
            extra_markup_mode: storeSettingsDraft.extra_markup_enabled ? "percent" : null,
            extra_markup_value:
                String(storeSettingsDraft.extra_markup_value ?? "").trim() === ""
                    ? null
                    : Number(storeSettingsDraft.extra_markup_value),
            extra_markup_min:
                String(storeSettingsDraft.extra_markup_min ?? "").trim() === ""
                    ? null
                    : Number(storeSettingsDraft.extra_markup_min),
            extra_markup_max:
                String(storeSettingsDraft.extra_markup_max ?? "").trim() === ""
                    ? null
                    : Number(storeSettingsDraft.extra_markup_max),
            dumping_mode: Boolean(storeSettingsDraft.dumping_mode),
        };
    };

    const handleStoreSettingsSave = async () => {
        setStoreSettingsSaveError("");
        setStoreSettingsSaveSuccess("");
        if (!selectedCode) {
            setStoreSettingsSaveError("Сначала выберите отправителя.");
            return;
        }
        if (!selectedStoreId) {
            setStoreSettingsSaveError("Выберите магазин.");
            return;
        }

        let payload;
        try {
            payload = buildStoreSettingsPayload();
        } catch (error) {
            setStoreSettingsSaveError(error.message || "Не удалось подготовить store-specific настройки.");
            return;
        }

        try {
            const saved = await upsertBusinessStoreSupplierSettings(selectedStoreId, selectedCode, payload);
            const selectedStoreRow = businessStores.find((item) => String(item.id) === String(selectedStoreId));
            setStoreSettingsDraft(buildStoreSettingsDraft(selectedCode, saved));
            setStoreSettingsOverview((prev) => {
                const next = prev.filter((item) => String(item.store_id) !== String(selectedStoreId));
                next.push({
                    ...saved,
                    store_id: Number(selectedStoreId),
                    store_code: selectedStoreRow?.store_code || "",
                    store_name: selectedStoreRow?.store_name || "",
                    enterprise_code: selectedStoreRow?.enterprise_code || null,
                    tabletki_branch: selectedStoreRow?.tabletki_branch || null,
                });
                next.sort((left, right) => String(left.store_name || "").localeCompare(String(right.store_name || "")));
                return next;
            });
            setStoreSettingsSaveSuccess("Настройки поставщика для выбранного магазина сохранены.");
        } catch (error) {
            console.error("Ошибка сохранения store-specific supplier settings:", error);
            const detail = error?.response?.data?.detail;
            setStoreSettingsSaveError(
                typeof detail === "string" && detail ? detail : "Не удалось сохранить настройки поставщика для магазина.",
            );
        }
    };

    const handleSave = async () => {
        setSaveError("");
        setSaveSuccess("");

        const payload = buildPayload();
        if (!payload.code || !payload.name) {
            setSaveError("Код и название отправителя обязательны.");
            return;
        }
        if (payload.is_active && !payload.city) {
            setSaveError("Выберите город для заказов SalesDrive.");
            return;
        }

        try {
            if (isCreating) {
                await createSupplier(payload);
                setSaveSuccess("Отправитель создан.");
            } else {
                await updateSupplier(selectedCode, payload);
                setSaveSuccess("Изменения сохранены.");
            }

            await loadList();
            setIsCreating(false);
            setSelectedCode(payload.code);
        } catch (error) {
            console.error("Ошибка сохранения поставщика:", error);
            const detail = error?.response?.data?.detail;
            setSaveError(
                typeof detail === "string" && detail
                    ? detail
                    : "Не удалось сохранить поставщика.",
            );
        }
    };

    const addCounterpartyLink = async () => {
        setCounterpartyError("");
        setCounterpartySuccess("");
        if (!selectedCode) {
            setCounterpartyError("Сначала выберите отправителя.");
            return;
        }
        if (!selectedCounterpartyId) {
            setCounterpartyError("Выберите контрагента.");
            return;
        }
        try {
            await upsertCounterpartyDropshipLink(selectedCounterpartyId, {
                supplier_code: selectedCode,
                is_default: linkedCounterparties.length === 0,
                is_active: true,
                priority: linkedCounterparties.length === 0 ? 10 : 100,
            });
            setSelectedCounterpartyId("");
            setCounterpartySuccess("Контрагент связан с отправителем.");
            await loadCounterparties();
        } catch (error) {
            console.error("Ошибка связи контрагента с отправителем:", error);
            setCounterpartyError(error?.response?.data?.detail || "Не удалось связать контрагента.");
        }
    };

    const removeCounterpartyLink = async (counterpartyId, linkId) => {
        setCounterpartyError("");
        setCounterpartySuccess("");
        try {
            await deleteCounterpartyDropshipLink(counterpartyId, linkId);
            setCounterpartySuccess("Связь с контрагентом удалена.");
            await loadCounterparties();
        } catch (error) {
            console.error("Ошибка удаления связи контрагента:", error);
            setCounterpartyError(error?.response?.data?.detail || "Не удалось удалить связь.");
        }
    };

    return (
        <div style={pageStyle} className="suppliers-page">
            <div style={{ ...cardStyle, padding: "20px 24px" }}>
                <h1 style={{ margin: 0, fontSize: "28px", color: "#111827" }}>Поставщики</h1>
            </div>

            <div className="sp-subtabs">
                {[
                    ["dropship", "Отправители"],
                    ["markups", "Наценки"],
                    ["deposits", "Депозиты"],
                    ["reconciliation", "Сверка"],
                    ["wholesale", "Поставщики опт"],
                    ["counterparties", "Контрагенты"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setActiveSection(key)}
                        className={activeSection === key ? "sp-subtab active" : "sp-subtab"}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {activeSection === "deposits" ? (
                <SupplierDepositsPanel suppliers={suppliers} />
            ) : activeSection === "markups" ? (
                <SupplierMarkupsPanel onOpenStoreSettings={openStoreSettings} />
            ) : activeSection === "reconciliation" ? (
                <SupplierReconciliationPanel />
            ) : activeSection === "dropship" ? (
            <div className="sp-split">
                <aside className="sp-card sp-picker">
                    <h2>Выбор отправителя</h2>

                    <input
                        className="sp-inp"
                        placeholder="Поиск по коду или названию…"
                        value={senderQuery}
                        onChange={(event) => setSenderQuery(event.target.value)}
                    />

                    <div className="sp-picker-actions">
                        <button type="button" onClick={resetCreate} className="sp-btn sp-btn-primary sp-btn-sm">
                            + Новый
                        </button>
                        <button type="button" onClick={loadList} className="sp-btn sp-btn-ghost sp-btn-sm">
                            Обновить
                        </button>
                    </div>

                    <label className="sp-switch-row">
                        <input
                            type="checkbox"
                            checked={showOnlyActive}
                            onChange={(event) => setShowOnlyActive(event.target.checked)}
                        />
                        <span>Только активные</span>
                    </label>

                    {listLoading ? <div className="sp-mini">Загрузка отправителей…</div> : null}
                    {listError ? <div className="sp-msg err">{listError}</div> : null}
                    {!listLoading && !listError && filteredSuppliers.length === 0 ? (
                        <div className="sp-mini">
                            {showOnlyActive ? "Нет активных отправителей." : "Отправители не найдены."}
                        </div>
                    ) : null}

                    <div className="sp-sender-list">
                        {filteredSuppliers.map((supplier) => {
                            const selected = supplier.code === selectedCode && !isCreating;
                            return (
                                <button
                                    key={supplier.code}
                                    type="button"
                                    onClick={() => selectSupplier(supplier.code)}
                                    className={selected ? "sp-sender-item sel" : "sp-sender-item"}
                                >
                                    <span className="nm">{supplier.display_name}</span>
                                    <span className="meta">
                                        Код {supplier.code} · магазинов: {supplier.connected_stores_count || 0}
                                    </span>
                                    <span className="meta">
                                        {truncateText(supplier.connected_stores_summary || "Магазины не подключены", 70)}
                                    </span>
                                    <span className={supplier.is_active ? "sp-badge on" : "sp-badge off"}>
                                        <span className="dot" />
                                        {supplier.is_active ? "Активен" : "Неактивен"}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <section className="sp-detail">
                    <div className="sp-card sp-detail-head">
                        <div className="sp-avatar">
                            {((isCreating ? "+" : (draft.name || draft.code || "?")).trim().charAt(0) || "?").toUpperCase()}
                        </div>
                        <div className="sp-who">
                            <span className="t">
                                {isCreating ? "Новый отправитель" : (draft.name || cardTitle)}
                                {!isCreating && draft.code ? (
                                    <span className="sp-mini" style={{ fontWeight: 600 }}> ({draft.code})</span>
                                ) : null}
                            </span>
                            {!isCreating && selectedListItem ? (
                                <span className="s">
                                    <span className={selectedListItem.is_active ? "sp-badge on" : "sp-badge off"}>
                                        <span className="dot" />
                                        {selectedListItem.is_active ? "Активен" : "Неактивен"}
                                    </span>
                                    {detail?.source_summary ? <span>Источник: {detail.source_summary}</span> : null}
                                    {draft.salesdrive_supplier_id ? <span>· SalesDrive {draft.salesdrive_supplier_id}</span> : null}
                                </span>
                            ) : null}
                        </div>
                        <div className="sp-acts">
                            <button type="button" onClick={handleCancel} className="sp-btn sp-btn-ghost">Отмена</button>
                            <button type="button" onClick={handleSave} className="sp-btn sp-btn-primary">Сохранить</button>
                        </div>
                    </div>

                    {detailLoading ? (
                        <div className="sp-card" style={{ padding: "20px" }}>
                            <span className="sp-mini">Загрузка данных отправителя…</span>
                        </div>
                    ) : detailError ? (
                        <div className="sp-card" style={{ padding: "20px" }}>
                            <span className="sp-msg err">{detailError}</span>
                        </div>
                    ) : !showEditor ? (
                        isCreating ? null : (
                            <div className="sp-card" style={{ padding: "20px" }}>
                                <span className="sp-mini">Выберите отправителя из списка.</span>
                            </div>
                        )
                    ) : (
                        <>
                            {saveError ? (
                                <div className="sp-card" style={{ padding: "16px 20px" }}>
                                    <span className="sp-msg err">{saveError}</span>
                                </div>
                            ) : null}
                            {saveSuccess ? (
                                <div className="sp-card" style={{ padding: "16px 20px" }}>
                                    <span className="sp-msg ok">{saveSuccess}</span>
                                </div>
                            ) : null}

                            <div className="sp-card" style={{ padding: "8px" }}>
                                <div className="sp-tabbar" role="tablist">
                                    <button
                                        type="button"
                                        className={activeCardTab === "store" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("store")}
                                    >
                                        <span className="star">★</span>По магазину
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "profile" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("profile")}
                                    >
                                        Профиль
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "pricing" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("pricing")}
                                    >
                                        Источник и цены
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "cp" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("cp")}
                                    >
                                        Контрагенты <span className="count">{linkedCounterparties.length}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "orders" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("orders")}
                                    >
                                        Заказы и график
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "orders_export" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("orders_export")}
                                    >
                                        Выгрузка заказов
                                    </button>
                                    <button
                                        type="button"
                                        className={activeCardTab === "shipping_schedule" ? "sp-tab active" : "sp-tab"}
                                        onClick={() => setActiveCardTab("shipping_schedule")}
                                    >
                                        График отгрузки
                                    </button>
                                </div>
                            </div>

                            {activeCardTab === "store" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <div className="sp-block">
                                            <div className="sp-bh">
                                                <span className="ic">☰</span>
                                                <h4>Уже настроенные магазины</h4>
                                                {storeSettingsOverview.length > 0 ? (
                                                    <span className="hint">{storeSettingsOverview.length} override</span>
                                                ) : null}
                                            </div>
                                            {storeSettingsOverview.length === 0 ? (
                                                <div className="sp-mini">Для этого поставщика пока нет store-specific overrides.</div>
                                            ) : (
                                                <div className="sp-tbl-wrap">
                                                    <table className="sp-tbl">
                                                        <thead>
                                                            <tr>
                                                                <th>Магазин</th>
                                                                <th>Активен</th>
                                                                <th>RRP</th>
                                                                <th>Приор.</th>
                                                                <th>Мин. надб.</th>
                                                                <th>Расчёт opt (%)</th>
                                                                <th>Доп. наценка</th>
                                                                <th>Демпинг</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {storeSettingsOverview.map((item) => (
                                                                <tr key={`${item.store_id}-${item.id}`}>
                                                                    <td>
                                                                        <div style={{ fontWeight: 600 }}>{item.store_name || item.store_code}</div>
                                                                        <div className="sp-mini">
                                                                            {[item.store_code, item.tabletki_branch ? `Branch ${item.tabletki_branch}` : null]
                                                                                .filter(Boolean)
                                                                                .join(" · ")}
                                                                        </div>
                                                                    </td>
                                                                    <td>{boolPill(item.is_active)}</td>
                                                                    <td>{boolPill(item.is_rrp)}</td>
                                                                    <td>{item.priority_override ?? "—"}</td>
                                                                    <td>{item.min_markup_threshold ?? "—"}</td>
                                                                    <td>{item.price_opt_calc_percent ?? "fallback"}</td>
                                                                    <td>
                                                                        {item.extra_markup_enabled
                                                                            ? `${item.extra_markup_mode || "percent"} / ${item.extra_markup_value ?? "—"}`
                                                                            : "Выключена"}
                                                                    </td>
                                                                    <td>{boolPill(item.dumping_mode)}</td>
                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleStoreSelection(String(item.store_id))}
                                                                            className="sp-btn sp-btn-ghost sp-btn-sm"
                                                                        >
                                                                            Редактировать
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>

                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">▦</span><h4>Выбор магазина</h4></div>
                                            <div className="sp-grid2 sp-end">
                                                <SpSelect
                                                    label="Предприятие"
                                                    value={selectedStoreEnterpriseCode}
                                                    onChange={handleStoreEnterpriseSelection}
                                                    disabled={storesLoading || !selectedCode}
                                                    placeholder="Выберите предприятие"
                                                    options={businessStoreEnterpriseOptions.map((enterprise) => ({
                                                        value: enterprise.enterprise_code,
                                                        label: enterprise.label,
                                                    }))}
                                                />
                                                <SpSelect
                                                    label="Магазин"
                                                    value={selectedStoreId ? String(selectedStoreId) : ""}
                                                    onChange={handleStoreSelection}
                                                    disabled={storesLoading || !selectedCode || !selectedStoreEnterpriseCode}
                                                    placeholder="Выберите магазин"
                                                    options={filteredBusinessStores.map((store) => ({
                                                        value: String(store.id),
                                                        label: formatStoreLabel(store),
                                                    }))}
                                                />
                                            </div>
                                            {storesError ? <div className="sp-msg err" style={{ marginTop: "10px" }}>{storesError}</div> : null}
                                            {storeSettingsError ? <div className="sp-msg err" style={{ marginTop: "10px" }}>{storeSettingsError}</div> : null}
                                            {storeSettingsLoading ? <div className="sp-mini" style={{ marginTop: "10px" }}>Загрузка настроек магазина…</div> : null}
                                        </div>

                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">◉</span><h4>Статус в магазине</h4></div>
                                            <div className="sp-grid3">
                                                <SpCheck
                                                    label="Активный для магазина"
                                                    checked={Boolean(storeSettingsDraft.is_active)}
                                                    onChange={(value) => setStoreSettingsField("is_active", value)}
                                                />
                                                <SpInput
                                                    label="Приоритет"
                                                    type="number"
                                                    value={storeSettingsDraft.priority_override}
                                                    onChange={(value) => setStoreSettingsField("priority_override", value)}
                                                />
                                                <SpCheck
                                                    label="Режим демпинга"
                                                    checked={Boolean(storeSettingsDraft.dumping_mode)}
                                                    onChange={(value) => setStoreSettingsField("dumping_mode", value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="sp-block">
                                            <div className="sp-bh">
                                                <span className="ic">₴</span>
                                                <h4>Ценообразование для магазина</h4>
                                                <span className="hint">фикс. наценка &gt; диапазона min/max</span>
                                            </div>
                                            <div className="sp-grid2">
                                                <SpCheck
                                                    label="Есть РРЦ (RRP)"
                                                    checked={Boolean(storeSettingsDraft.is_rrp)}
                                                    onChange={(value) => setStoreSettingsField("is_rrp", value)}
                                                />
                                                <SpInput
                                                    label="Минимальная ценовая надбавка (обязательно)"
                                                    type="number"
                                                    value={storeSettingsDraft.min_markup_threshold}
                                                    onChange={(value) => setStoreSettingsField("min_markup_threshold", value)}
                                                />
                                                <SpInput
                                                    label={String(selectedCode).toUpperCase() === "D18" ? "Скидка от РРЦ (%)" : "Параметр для расчёта оптовой цены (%)"}
                                                    type="number"
                                                    value={storeSettingsDraft.price_opt_calc_percent}
                                                    onChange={(value) => setStoreSettingsField("price_opt_calc_percent", value)}
                                                />
                                                <SpCheck
                                                    label="Дополнительная наценка включена"
                                                    checked={Boolean(storeSettingsDraft.extra_markup_enabled)}
                                                    onChange={(value) => setStoreSettingsField("extra_markup_enabled", value)}
                                                />
                                                <SpInput
                                                    label="Режим дополнительной наценки"
                                                    value={storeSettingsDraft.extra_markup_mode || "percent"}
                                                    onChange={(value) => setStoreSettingsField("extra_markup_mode", value)}
                                                    disabled
                                                />
                                                <SpInput
                                                    label="Фиксированная дополнительная наценка (%)"
                                                    type="number"
                                                    value={storeSettingsDraft.extra_markup_value}
                                                    onChange={(value) => setStoreSettingsField("extra_markup_value", value)}
                                                />
                                                <SpInput
                                                    label="Минимум диапазона доп. наценки (%)"
                                                    type="number"
                                                    value={storeSettingsDraft.extra_markup_min}
                                                    onChange={(value) => setStoreSettingsField("extra_markup_min", value)}
                                                />
                                                <SpInput
                                                    label="Максимум диапазона доп. наценки (%)"
                                                    type="number"
                                                    value={storeSettingsDraft.extra_markup_max}
                                                    onChange={(value) => setStoreSettingsField("extra_markup_max", value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="sp-save-bar">
                                            <span className="note">Изменения применяются только к выбранному магазину</span>
                                            {storeSettingsSaveError ? <span className="sp-msg err">{storeSettingsSaveError}</span> : null}
                                            {storeSettingsSaveSuccess ? <span className="sp-msg ok">{storeSettingsSaveSuccess}</span> : null}
                                            <button
                                                type="button"
                                                onClick={handleStoreSettingsSave}
                                                className="sp-btn sp-btn-primary"
                                                disabled={!selectedStoreId || !selectedCode}
                                            >
                                                Сохранить настройки магазина
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "profile" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>Профиль отправителя</h3>
                                        <p className="lead">Идентификация и глобальные настройки, действующие по умолчанию для всех магазинов.</p>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">ⓘ</span><h4>Обзор</h4></div>
                                            <div className="sp-grid2">
                                                <SpReadField label="Код отправителя" value={draft.code} />
                                                <SpReadField label="Название" value={draft.name} />
                                                <SpReadField label="Статус" value={draft.is_active ? "Активный" : "Неактивный"} />
                                                <SpReadField label="Источник / подключение" value={detail?.source_summary} />
                                                <SpReadField label="Код SalesDrive / supplierlist" value={draft.salesdrive_supplier_id} span />
                                            </div>
                                        </div>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">⚙</span><h4>Глобальные настройки</h4></div>
                                            <div className="sp-grid2">
                                                <SpCheck
                                                    label="Активный (Active)"
                                                    checked={Boolean(draft.is_active)}
                                                    onChange={(value) => setField("is_active", value)}
                                                />
                                                <div />
                                                <SpInput
                                                    label="Код (Code)"
                                                    value={draft.code}
                                                    disabled={!isCreating}
                                                    onChange={(value) => setField("code", value)}
                                                />
                                                <SpInput
                                                    label="Название (Name)"
                                                    value={draft.name}
                                                    onChange={(value) => setField("name", value)}
                                                />
                                                <SpSelect
                                                    label="Город для заказов SalesDrive"
                                                    value={draft.city}
                                                    options={SUPPLIER_CITY_OPTIONS}
                                                    onChange={(value) => setField("city", value)}
                                                    placeholder="— выберите город —"
                                                />
                                                <SpInput
                                                    label="Код SalesDrive / supplierlist"
                                                    type="number"
                                                    value={draft.salesdrive_supplier_id}
                                                    onChange={(value) => setField("salesdrive_supplier_id", value)}
                                                    span
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "pricing" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>Источник и базовые цены</h3>
                                        <p className="lead">Откуда тянутся офферы и fallback-параметры ценообразования, если у магазина нет собственного override.</p>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">⇄</span><h4>Источник / подключение</h4></div>
                                            <div className="sp-grid2">
                                                <SpInput
                                                    label="Основной источник / URL / токен"
                                                    value={draft.feed_url}
                                                    onChange={(value) => setField("feed_url", value)}
                                                />
                                                <SpInput
                                                    label="Дополнительный источник / параметр"
                                                    value={draft.gdrive_folder}
                                                    onChange={(value) => setField("gdrive_folder", value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">%</span><h4>Базовые ценовые параметры</h4><span className="hint">fallback</span></div>
                                            <div className="sp-grid2">
                                                <SpInput
                                                    label={String(draft.code).toUpperCase() === "D18" ? "Скидка от РРЦ (%) — fallback" : "Параметр для расчёта оптовой цены (%) — fallback"}
                                                    type="number"
                                                    value={draft.profit_percent}
                                                    onChange={(value) => setField("profit_percent", value)}
                                                />
                                                <SpInput
                                                    label="Наценка для розничной цены (%)"
                                                    type="number"
                                                    value={draft.retail_markup}
                                                    onChange={(value) => setField("retail_markup", value)}
                                                />
                                                <SpInput
                                                    label="Минимальная ценовая надбавка"
                                                    type="number"
                                                    value={draft.min_markup_threshold}
                                                    onChange={(value) => setField("min_markup_threshold", value)}
                                                />
                                                <SpInput
                                                    label="Приоритет (Priority)"
                                                    type="number"
                                                    value={draft.priority}
                                                    onChange={(value) => setField("priority", value)}
                                                />
                                                <SpInput
                                                    label="Ярус приоритета публикации (0–9)"
                                                    type="number"
                                                    value={draft.publish_priority_tier}
                                                    onChange={(value) => setField("publish_priority_tier", value)}
                                                />
                                            </div>
                                            <p className="sp-hint" style={{ marginTop: "8px" }}>
                                                Ярус сравнивается раньше цены во всех каналах, если включён флаг «Ярус приоритета отправителей»
                                                в настройках Business. 0 — обычный отправитель; у собственных складов ярус выше.
                                            </p>
                                        </div>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">⚡</span><h4>Технические настройки</h4></div>
                                            <SpCheck
                                                label="Режим демпинга (Dumping mode) — глобальный"
                                                checked={Boolean(draft.use_feed_instead_of_gdrive)}
                                                onChange={(value) => setField("use_feed_instead_of_gdrive", value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "cp" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>Контрагенты отправителя</h3>
                                        <p className="lead">Юрлица, от имени которых выставляются документы для этого отправителя.</p>
                                        {counterpartyError ? <div className="sp-msg err" style={{ marginBottom: "12px" }}>{counterpartyError}</div> : null}
                                        {counterpartySuccess ? <div className="sp-msg ok" style={{ marginBottom: "12px" }}>{counterpartySuccess}</div> : null}
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">＋</span><h4>Связать контрагента</h4></div>
                                            <div className="sp-grid2 sp-end" style={{ gridTemplateColumns: "1fr auto" }}>
                                                <SpSelect
                                                    label="Контрагент"
                                                    value={selectedCounterpartyId}
                                                    onChange={setSelectedCounterpartyId}
                                                    placeholder="— выберите —"
                                                    options={unlinkedCounterparties.map((counterparty) => ({
                                                        value: String(counterparty.id),
                                                        label: `${counterparty.short_name || counterparty.legal_name}${counterparty.tax_id ? ` · ${counterparty.tax_id}` : ""}`,
                                                    }))}
                                                />
                                                <button
                                                    type="button"
                                                    className="sp-btn sp-btn-primary"
                                                    onClick={addCounterpartyLink}
                                                    style={{ alignSelf: "end", height: "40px" }}
                                                >
                                                    Связать
                                                </button>
                                            </div>
                                        </div>
                                        <div className="sp-tbl-wrap">
                                            <table className="sp-tbl">
                                                <thead>
                                                    <tr>
                                                        <th>Контрагент</th>
                                                        <th>ЕДРПОУ</th>
                                                        <th>Default</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {linkedCounterparties.map(({ counterparty, link }) => (
                                                        <tr key={link.id}>
                                                            <td>{counterparty.short_name || counterparty.legal_name}</td>
                                                            <td>{counterparty.tax_id || "—"}</td>
                                                            <td>{link.is_default ? <span className="sp-pill g">yes</span> : ""}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="sp-btn sp-btn-ghost sp-btn-sm"
                                                                    onClick={() => removeCounterpartyLink(counterparty.id, link.id)}
                                                                >
                                                                    Убрать связь
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {linkedCounterparties.length === 0 ? (
                                                        <tr><td colSpan={4} className="sp-mini">Контрагенты пока не связаны.</td></tr>
                                                    ) : null}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "orders" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>Заказы и график доступности</h3>
                                        <p className="lead">Участие поставщика в обработке заказов и окно, в котором его офферы временно не обновляются.</p>
                                        {!Boolean(draft.is_active) ? (
                                            <div className="sp-callout">
                                                <span className="ic">ⓘ</span>
                                                <div>Отправитель глобально отключён. Он не участвует в публикации остатков, выборе поставщика и обработке заказов; настройки ниже сохраняются для будущего включения.</div>
                                            </div>
                                        ) : null}
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">📦</span><h4>Заказы</h4></div>
                                            <div className="sp-grid2">
                                                <SpCheck
                                                    label="Участвует в обработке заказов"
                                                    checked={Boolean(draft.biotus_orders_enabled)}
                                                    onChange={(value) => setField("biotus_orders_enabled", value)}
                                                />
                                                <SpCheck
                                                    label="Fulfillment-режим Новой Почты"
                                                    checked={Boolean(draft.np_fulfillment_enabled)}
                                                    onChange={(value) => setField("np_fulfillment_enabled", value)}
                                                />
                                                <SpSelect
                                                    label="ТТН"
                                                    value={draft.order_ttn_mode || "auto"}
                                                    onChange={(value) => setField("order_ttn_mode", value)}
                                                    placeholder={null}
                                                    options={[
                                                        { value: "auto", label: "Создаём автоматически" },
                                                        { value: "manual_later", label: "Добавляем позже вручную" },
                                                    ]}
                                                />
                                            </div>
                                            <p className="lead">
                                                «Позже»: заказ переводится в статус 21 без ТТН, ТТН вносят в SalesDrive вручную. На площадке можно переопределить в таблице отправителей. Заказы Kasta с «позже» обработчик не берёт — их ведут вручную.
                                            </p>
                                        </div>
                                        <div className="sp-block">
                                            <div className="sp-bh"><span className="ic">⏱</span><h4>График недоступности</h4></div>
                                            <div className="sp-callout">
                                                <span className="ic">ⓘ</span>
                                                <div>В заданном окне поставщик считается временно недоступным и его офферы <b>не обновляются</b> в dropship pipeline. Заполнять нужно все 4 поля.</div>
                                            </div>
                                            <SpCheck
                                                label="Включить окно недоступности (Schedule enabled)"
                                                checked={Boolean(draft.schedule_enabled)}
                                                onChange={(value) => setField("schedule_enabled", value)}
                                            />
                                            <div className="sp-grid2" style={{ marginTop: "12px" }}>
                                                <SpSelect
                                                    label="Начало: день"
                                                    value={draft.block_start_day}
                                                    disabled={!draft.schedule_enabled}
                                                    options={DAY_OPTIONS}
                                                    onChange={(value) => setField("block_start_day", value)}
                                                />
                                                <SpInput
                                                    label="Начало: время"
                                                    type="time"
                                                    value={draft.block_start_time}
                                                    disabled={!draft.schedule_enabled}
                                                    onChange={(value) => setField("block_start_time", value)}
                                                />
                                                <SpSelect
                                                    label="Конец: день"
                                                    value={draft.block_end_day}
                                                    disabled={!draft.schedule_enabled}
                                                    options={DAY_OPTIONS}
                                                    onChange={(value) => setField("block_end_day", value)}
                                                />
                                                <SpInput
                                                    label="Конец: время"
                                                    type="time"
                                                    value={draft.block_end_time}
                                                    disabled={!draft.schedule_enabled}
                                                    onChange={(value) => setField("block_end_time", value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "orders_export" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>Выгрузка заказов</h3>
                                        <p className="lead">
                                            Excel содержит заказы за выбранную дату со статусами «Обработано», «В дороге НП», «Прибыл НП» и «Получено без наложки».
                                            В смешанных заказах выгружаются только товары этого поставщика.
                                        </p>
                                        <div className="sp-grid2 sp-end" style={{ maxWidth: "620px" }}>
                                            <SpInput
                                                label="Дата создания заказов"
                                                type="date"
                                                value={ordersExportDate}
                                                onChange={setOrdersExportDate}
                                                disabled={ordersExportLoading}
                                            />
                                            <button
                                                type="button"
                                                className="sp-btn sp-btn-primary"
                                                style={{ alignSelf: "end", height: "40px" }}
                                                disabled={!selectedCode || !ordersExportDate || ordersExportLoading}
                                                onClick={async () => {
                                                    setOrdersExportLoading(true);
                                                    setOrdersExportError("");
                                                    setOrdersExportMessage("");
                                                    try {
                                                        const rowsCount = await downloadSupplierOrdersExport({
                                                            supplierCode: selectedCode,
                                                            supplierName: draft.name,
                                                            date: ordersExportDate,
                                                        });
                                                        if (rowsCount === 0) {
                                                            setOrdersExportMessage("За выбранную дату подходящих заказов нет. Скачан пустой файл с заголовками и итогом.");
                                                        }
                                                    } catch (error) {
                                                        console.error("Ошибка выгрузки заказов поставщика:", error);
                                                        setOrdersExportError(error?.response?.data?.detail || "Не удалось сформировать Excel-файл.");
                                                    } finally {
                                                        setOrdersExportLoading(false);
                                                    }
                                                }}
                                            >
                                                {ordersExportLoading ? "Формирование…" : "Скачать Excel"}
                                            </button>
                                        </div>
                                        {ordersExportError ? <div className="sp-msg err" style={{ marginTop: "12px" }}>{ordersExportError}</div> : null}
                                        {ordersExportMessage ? <div className="sp-msg ok" style={{ marginTop: "12px" }}>{ordersExportMessage}</div> : null}
                                    </div>
                                </div>
                            ) : null}

                            {activeCardTab === "shipping_schedule" ? (
                                <div className="sp-card">
                                    <div className="sp-panel">
                                        <h3>График отгрузки</h3>
                                        <p className="lead">Справочная настройка в часовом поясе Киева. Она пока не меняет обработку заказов.</p>
                                        <div className="sp-callout">
                                            <span className="ic">ⓘ</span>
                                            <div>«Не задано» означает, что данных нет. Это не равно «Не отправляет». Для режима «По расписанию» укажите оба времени.</div>
                                        </div>
                                        <div style={{ overflowX: "auto", marginTop: "16px" }}>
                                            <table className="sp-table">
                                                <thead>
                                                    <tr>
                                                        <th>День</th>
                                                        <th>Режим</th>
                                                        <th>Принимает заказы до</th>
                                                        <th>Время отправки</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {normalizeShippingSchedule(draft.shipping_schedule).map((item) => {
                                                        const scheduled = item.mode === "scheduled";
                                                        return (
                                                            <tr key={item.weekday}>
                                                                <td>{SHIPPING_SCHEDULE_DAY_LABELS[item.weekday - 1]}</td>
                                                                <td>
                                                                    <select
                                                                        className="sp-inp"
                                                                        value={item.mode}
                                                                        onChange={(event) => setShippingScheduleDay(item.weekday, "mode", event.target.value)}
                                                                    >
                                                                        {SHIPPING_SCHEDULE_MODE_OPTIONS.map((option) => (
                                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        className="sp-inp"
                                                                        type="time"
                                                                        value={item.order_cutoff_time}
                                                                        disabled={!scheduled}
                                                                        onChange={(event) => setShippingScheduleDay(item.weekday, "order_cutoff_time", event.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        className="sp-inp"
                                                                        type="time"
                                                                        value={item.shipping_time}
                                                                        disabled={!scheduled}
                                                                        onChange={(event) => setShippingScheduleDay(item.weekday, "shipping_time", event.target.value)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </section>
            </div>
            ) : activeSection === "wholesale" ? (
                <FulfillmentPage embedded mode="wholesale" />
            ) : (
                <CounterpartiesPanel />
            )}
        </div>
    );
};

export default SuppliersPage;
